import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import client from "../../../../api/client";

const blankQuestion = () => ({ text: "", type: "single", explanation: "", options: [{ text: "", correct: true }, { text: "", correct: false }] });
const errMsg = (e, fallback) => {
  const d = e?.response?.data;
  return (d?.errors && Object.values(d.errors)[0]?.[0]) || d?.message || fallback;
};

/** Quiz builder: settings plus single- or multiple-answer questions. Questions lock once students attempt it. */
export default function StaffQuizPage() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    client.get(`/staff/quizzes/${quizId}`).then((r) => setQ({ ...r.data, questions: r.data.questions.length ? r.data.questions : [blankQuestion()] }))
      .catch((e) => setError(e?.response?.data?.message || "We couldn't load this quiz."));
  }, [quizId]);

  if (error) return <div className="lms"><div className="main single"><div className="view"><section className="complete-box"><div><strong>{error}</strong><small>Go back to the week and try again.</small></div><Link className="cbtn quiet" to="/dashboard/staff/plans">Back to plans</Link></section></div></div></div>;
  if (!q) return <div className="lms"><div className="main single"><div className="view"><div className="skeleton" style={{ height: 120 }} /><div className="skeleton" style={{ height: 320 }} /></div></div></div>;

  const set = (k, v) => setQ({ ...q, [k]: v });
  const setQn = (i, patch) => set("questions", q.questions.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const setOpt = (i, k, patch) => setQn(i, { options: q.questions[i].options.map((o, j) => (j === k ? { ...o, ...patch } : o)) });
  const markRight = (i, k) => {
    const qq = q.questions[i];
    setQn(i, { options: qq.options.map((o, j) => (qq.type === "single" ? { ...o, correct: j === k } : j === k ? { ...o, correct: !o.correct } : o)) });
  };
  const setType = (i, type) => {
    let seen = false;
    const options = q.questions[i].options.map((o) => { if (type === "single" && o.correct) { const keep = !seen; seen = true; return { ...o, correct: keep }; } return o; });
    setQn(i, { type, options });
  };
  const weekUrl = `/dashboard/staff/weeks/${q.week.id}`;

  const save = async (publish = q.published) => {
    setBusy(true);
    try {
      const body = {
        title: q.title, description: q.description || null, minutes: +q.minutes || 20, attempts: +q.attempts || 1, pass: +q.pass || 60,
        published: publish, opens_at: q.opens_at || null, closes_at: q.closes_at || null,
        questions: q.questions.filter((x) => x.text.trim()).map((x) => ({ text: x.text.trim(), type: x.type, explanation: x.explanation || null, options: x.options.filter((o) => o.text.trim()).map((o) => ({ text: o.text.trim(), correct: !!o.correct })) })),
      };
      const r = await client.put(`/staff/quizzes/${quizId}`, body);
      setQ({ ...q, published: publish });
      toast.success(r.data.message);
    } catch (e) {
      toast.error(errMsg(e, "We couldn't save the quiz."));
    }
    setBusy(false);
  };
  const remove = async () => {
    try { await client.delete(`/staff/quizzes/${quizId}`); toast.success("Quiz deleted"); navigate(weekUrl); }
    catch (e) { toast.error(errMsg(e, "We couldn't delete the quiz.")); setConfirmDelete(false); }
  };

  return (
    <div className="lms"><div className="main single" style={{ maxWidth: 1000 }}><div className="view">
      <div className="pagebar"><nav className="trail" aria-label="You are here"><Link to="/dashboard/staff/plans">Course plans</Link><span className="sep">›</span><Link to={`/dashboard/staff/plans/${q.week.cohort_id}/${q.week.course_id}`}>Plan</Link><span className="sep">›</span><Link to={weekUrl}>Week</Link><span className="sep">›</span><span className="here">Quiz</span></nav></div>
      <div className="sp-top">
        <div><span className="sp-kicker">Quiz</span><h1>{q.title || "Untitled quiz"}</h1><p>Scores count toward the activities average. Students see it between the open and close times once it's open to students.</p></div>
        <div className="sp-filters">
          <button className="cbtn quiet" type="button" onClick={() => save(!q.published)} disabled={busy}>{q.published ? "Close to students" : "Open to students"}</button>
          <button className="cbtn go" type="button" onClick={() => save()} disabled={busy}>{busy ? "Saving…" : "Save quiz"}</button>
        </div>
      </div>
      {q.published ? <section className="sp-note"><b>Open to students.</b> They can take it between the open and close times below.</section> : <section className="sp-note"><b>Draft.</b> Students can't see it until you open it.</section>}
      {q.locked && <div className="sp-warn">Students have attempted this quiz, so its questions are locked. You can still change the title, times and attempts.</div>}

      <section className="sp-box sp-box-b">
        <div className="sp-form four">
          <label className="sp-field" htmlFor="qz-title">Title<input id="qz-title" value={q.title} onChange={(e) => set("title", e.target.value)} /></label>
          <label className="sp-field" htmlFor="qz-min">Time limit (minutes)<input id="qz-min" type="number" min="1" value={q.minutes ?? ""} onChange={(e) => set("minutes", e.target.value)} /></label>
          <label className="sp-field" htmlFor="qz-att">Attempts allowed<input id="qz-att" type="number" min="1" value={q.attempts ?? ""} onChange={(e) => set("attempts", e.target.value)} /></label>
          <label className="sp-field" htmlFor="qz-pass">Pass mark (%)<input id="qz-pass" type="number" min="1" max="100" value={q.pass ?? ""} onChange={(e) => set("pass", e.target.value)} /></label>
          <label className="sp-field" htmlFor="qz-open">Opens (WAT)<input id="qz-open" type="datetime-local" value={q.opens_at || ""} onChange={(e) => set("opens_at", e.target.value)} /></label>
          <label className="sp-field" htmlFor="qz-close">Closes (WAT)<input id="qz-close" type="datetime-local" value={q.closes_at || ""} onChange={(e) => set("closes_at", e.target.value)} /></label>
        </div>
      </section>

      {q.questions.map((qq, i) => (
        <section key={i} className="sp-box">
          <div className="sp-box-h"><h2>Question {i + 1}</h2><span className="sp-acts">
            <select className="sp-select" aria-label="Answer type" value={qq.type} onChange={(e) => setType(i, e.target.value)} disabled={q.locked}><option value="single">One correct answer</option><option value="multiple">More than one correct answer</option></select>
            {!q.locked && q.questions.length > 1 && <button type="button" className="link-btn danger" onClick={() => set("questions", q.questions.filter((_, j) => j !== i))}>Delete</button>}
          </span></div>
          <fieldset className="sp-box-b sp-stack" disabled={q.locked} style={{ border: 0, margin: 0 }}>
            <label className="sp-field" htmlFor={`qq-${i}`}>Question<textarea id={`qq-${i}`} rows={2} value={qq.text} onChange={(e) => setQn(i, { text: e.target.value })} /></label>
            <div className="sp-opts">
              {qq.options.map((o, k) => (
                <div key={k} className={`sp-opt-row ${o.correct ? "right" : ""}`}>
                  <input type={qq.type === "single" ? "radio" : "checkbox"} name={`right-${i}`} aria-label={`Option ${k + 1} is correct`} checked={!!o.correct} onChange={() => markRight(i, k)} />
                  <input aria-label={`Option ${k + 1}`} value={o.text} onChange={(e) => setOpt(i, k, { text: e.target.value })} placeholder={`Option ${k + 1}`} />
                  {qq.options.length > 2 && <button type="button" className="link-btn danger" onClick={() => setQn(i, { options: qq.options.filter((_, j) => j !== k) })}>Remove</button>}
                </div>
              ))}
            </div>
            <div className="sp-acts"><button type="button" className="link-btn" onClick={() => setQn(i, { options: [...qq.options, { text: "", correct: false }] })}>+ Add option</button><small className="course-tag">Tick the correct {qq.type === "single" ? "answer" : "answers"}.</small></div>
            <label className="sp-field" htmlFor={`qe-${i}`}>Explanation shown after the quiz (optional)<input id={`qe-${i}`} value={qq.explanation || ""} onChange={(e) => setQn(i, { explanation: e.target.value })} /></label>
          </fieldset>
        </section>
      ))}

      <div className="sp-acts">
        {!q.locked && <button className="cbtn quiet" type="button" onClick={() => set("questions", [...q.questions, blankQuestion()])}>+ Add question</button>}
        <span style={{ marginLeft: "auto" }} />
        {confirmDelete ? <><span className="course-tag">Delete this quiz?</span><button className="cbtn quiet" type="button" onClick={() => setConfirmDelete(false)}>Cancel</button><button className="cbtn danger" type="button" onClick={remove}>Delete quiz</button></>
          : <button className="link-btn danger" type="button" onClick={() => setConfirmDelete(true)}>Delete quiz</button>}
        <button className="cbtn go" type="button" onClick={() => save()} disabled={busy}>{busy ? "Saving…" : "Save quiz"}</button>
      </div>
    </div></div></div>
  );
}
